import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';
import { extractStrategyRows, getActiveStrategies, normalizeStrategies } from '../../api/strategyLibraryService';
import './FetchStrategyData.css';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const FALLBACK_STRATEGIES = [
  { name: 'Bottom Fishing', slug: 'bottomfishing', chartinkUrl: 'https://chartink.com/screener/chart-story-bottom-fishing' },
  { name: 'Parabolic', slug: 'parabolic', chartinkUrl: 'https://chartink.com/screener/cs-momemtume-catching-version-2' },
  { name: 'Exit', slug: 'exit', chartinkUrl: 'https://chartink.com/screener/exit-42003079' },
  { name: 'Fundamental', slug: 'fundamental', chartinkUrl: 'https://chartink.com/screener/copy-low-pe-high-eps-cs' },
  { name: 'RSC', slug: 'rsc', chartinkUrl: 'https://chartink.com/screener/copy-relative-strength-comparision-4' },
  { name: 'M Fibo', slug: 'mfibo', chartinkUrl: 'https://chartink.com/screener/m-fibo-chartstory' },
  { name: 'AB Candle +', slug: 'dailyabcandlepositive', chartinkUrl: 'https://chartink.com/screener/abnormal-candle-ve' },
  { name: 'AB Candle -', slug: 'dailyabcandlenegative', chartinkUrl: 'https://chartink.com/screener/abnormal-candle-ve-2' },
];

const LS_KEY = 'chartink_dashboard_urls';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function extractSlug(input) {
  const str = input.trim();
  const match = str.match(/chartink\.com\/screener\/([^/?#\s]+)/i);
  return match ? match[1].toLowerCase() : str.toLowerCase().replace(/[^a-z0-9-]/g, '-');
}

function normaliseUrl(raw) {
  const slug = extractSlug(raw);
  return raw.trim().startsWith('http') ? raw.trim() : `https://chartink.com/screener/${slug}`;
}

function formatVol(v) {
  if (!v) return '—';
  if (v >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(1) + 'L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return String(v);
}

function loadFromLS() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    return saved.map(e => {
      const entry = typeof e === 'string' ? { url: e, strategyCollection: '', syncMode: 'replace' } : e;
      const slug  = extractSlug(entry.url);
      return { slug, url: normaliseUrl(entry.url), strategyCollection: entry.strategyCollection || '', syncMode: 'replace', status: 'idle', stocks: [], error: null, sync: null };
    });
  } catch { return []; }
}

function saveToLS(items) {
  localStorage.setItem(LS_KEY, JSON.stringify(items.map(i => ({ url: i.url, strategyCollection: i.strategyCollection, syncMode: i.syncMode }))));
}

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

/* Screener accordion block */
function ScreenerBlock({ result }) {
  const [open, setOpen]     = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const stocks = useMemo(() => result.stocks || [], [result.stocks]);

  const sorted = useCallback(() => {
    let list = [...stocks];
    if (sortCol) {
      list.sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [stocks, sortCol, sortDir]);

  const visible = sorted().filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.nsecode || '').toLowerCase().includes(q) || (s.stockName || '').toLowerCase().includes(q);
  });

  const maxVol = Math.max(...stocks.map(s => s.volume || 0), 1);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const thCls = (col) => sortCol === col ? (sortDir === 'asc' ? 'sort-asc' : 'sort-desc') : '';

  const fetchedAt = result.fetchedAt ? new Date(result.fetchedAt).toLocaleTimeString('en-IN') : '';

  if (!result.success) {
    return (
      <div className={`fsd-screener-block${open ? ' open' : ''}`}>
        <div className="fsd-screener-header" onClick={() => setOpen(v => !v)}>
          <div className="fsd-screener-left">
            <div className="fsd-dot error" />
            <div>
              <div className="fsd-screener-name">{result.slug}</div>
              <div className="fsd-screener-meta">Fetch failed</div>
            </div>
          </div>
          <div className="fsd-screener-right">
            <span className="fsd-chevron">▼</span>
          </div>
        </div>
        <div className="fsd-screener-body">
          <div className="fsd-err-row">⚠ {result.error || 'Unknown error'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fsd-screener-block${open ? ' open' : ''}`}>
      <div className="fsd-screener-header" onClick={() => setOpen(v => !v)}>
        <div className="fsd-screener-left">
          <div className="fsd-dot success" />
          <div>
            <div className="fsd-screener-name">{result.screenerName || result.slug}</div>
            <div className="fsd-screener-meta">{result.slug} · fetched {fetchedAt}</div>
          </div>
        </div>
        <div className="fsd-screener-right">
          <span className="fsd-count-badge">{stocks.length} stocks</span>
          <span className="fsd-chevron">▼</span>
        </div>
      </div>

      <div className="fsd-screener-body">
        <div className="fsd-table-toolbar">
          <input
            className="fsd-table-search"
            placeholder="Search symbol or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="fsd-count-label">{visible.length} stocks</span>
        </div>

        <div className="fsd-table-wrap">
          <table className="fsd-table">
            <thead>
              <tr>
                <th className={thCls('sr')}         onClick={() => handleSort('sr')}>#</th>
                <th className={thCls('nsecode')}     onClick={() => handleSort('nsecode')}>Symbol</th>
                <th className={thCls('stockName')}   onClick={() => handleSort('stockName')}>Company</th>
                <th className={thCls('close')}       onClick={() => handleSort('close')}>Price ₹</th>
                <th className={thCls('percentChange')} onClick={() => handleSort('percentChange')}>% Change</th>
                <th className={thCls('volume')}      onClick={() => handleSort('volume')}>Volume</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem' }}>No stocks</td></tr>
              ) : visible.map((s, i) => {
                const pct  = s.percentChange ?? 0;
                const cls  = pct >= 0 ? 'pos' : 'neg';
                const sign = pct >= 0 ? '+' : '';
                const vPct = Math.round(((s.volume || 0) / maxVol) * 100);
                return (
                  <tr key={i}>
                    <td>{s.sr ?? i + 1}</td>
                    <td className="fsd-sym">{s.nsecode || '—'}</td>
                    <td className="fsd-name" title={s.stockName}>{s.stockName || '—'}</td>
                    <td className="fsd-price">₹{(s.close ?? 0).toFixed(2)}</td>
                    <td className={`fsd-chg ${cls}`}>{sign}{pct.toFixed(2)}%</td>
                    <td>
                      <div className="fsd-vol-wrap">
                        <span>{formatVol(s.volume)}</span>
                        <div className="fsd-vol-bar"><div className="fsd-vol-fill" style={{ width: `${vPct}%` }} /></div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Toast hook
───────────────────────────────────────────── */
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = 'info') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);
  return { toasts, show };
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
export default function FetchStrategyData() {
  const [urlItems, setUrlItems]     = useState(() => loadFromLS());
  const [results, setResults]       = useState([]);   // result objects from fetch-all
  const [strategies, setStrategies] = useState(FALLBACK_STRATEGIES);
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [fetching, setFetching]     = useState(false);
  const [progress, setProgress]     = useState({ done: 0, total: 0 });
  const [lastFetch, setLastFetch]   = useState(null);
  const [urlInput, setUrlInput]     = useState('');
  const { toasts, show: showToast } = useToasts();
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    getActiveStrategies()
      .then(({ data }) => {
        if (!mounted) return;
        const rows = normalizeStrategies(extractStrategyRows(data)).filter(s => s.slug && s.chartinkUrl);
        if (rows.length) setStrategies(rows);
      })
      .catch(() => {})
      .finally(() => mounted && setStrategyLoading(false));
    return () => { mounted = false; };
  }, []);

  /* persist on change */
  useEffect(() => { saveToLS(urlItems); }, [urlItems]);

  /* ── helpers ── */
  const addUrlToList = useCallback((url, collection = '', syncMode = 'replace') => {
    const slug = extractSlug(url);
    if (!slug) return false;
    const normUrl = normaliseUrl(url);
    setUrlItems(prev => {
      if (prev.find(i => i.slug === slug)) return prev;
      return [...prev, { slug, url: normUrl, strategyCollection: collection, syncMode, status: 'idle', stocks: [], error: null, sync: null }];
    });
    return true;
  }, []);

  const handleAddUrl = () => {
    const val = urlInput.trim();
    if (!val) return;
    const slug = extractSlug(val);
    if (urlItems.find(i => i.slug === slug)) { showToast('Screener already in list', 'info'); return; }
    addUrlToList(val);
    setUrlInput('');
    inputRef.current?.focus();
  };

  const removeUrl = (slug) => {
    setUrlItems(prev => prev.filter(i => i.slug !== slug));
    setResults(prev => prev.filter(r => r.slug !== slug));
  };

  const clearAll = () => {
    if (!urlItems.length) return;
    setUrlItems([]);
    setResults([]);
  };

  const setCollection = (slug, value) => {
    setUrlItems(prev => prev.map(i => i.slug === slug ? { ...i, strategyCollection: value } : i));
  };

  const setSyncMode = (slug, mode) => {
    setUrlItems(prev => prev.map(i => i.slug === slug ? { ...i, syncMode: mode } : i));
  };

  /* ── preset helpers ── */
  const addPreset = (strategy) => {
    const url = strategy.chartinkUrl;
    const slug = extractSlug(url);
    if (urlItems.find(i => i.slug === slug)) { showToast(`${slug} already in list`, 'info'); return; }
    addUrlToList(url, strategy.slug, 'replace');
    showToast(`Added: ${strategy.name}`, 'success');
  };

  const addAllPresets = () => {
    let added = 0;
    strategies.forEach(strategy => {
      const slug = extractSlug(strategy.chartinkUrl);
      if (!urlItems.find(i => i.slug === slug)) {
        addUrlToList(strategy.chartinkUrl, strategy.slug, 'replace');
        added++;
      }
    });
    showToast(added ? `Added ${added} strategies` : 'All strategies already in list', added ? 'success' : 'info');
  };

  /* ── fetch all ── */
  const fetchAll = async () => {
    if (!urlItems.length || fetching) return;

    setFetching(true);
    setProgress({ done: 0, total: urlItems.length });
    setUrlItems(prev => prev.map(i => ({ ...i, status: 'fetching' })));

    const slugs = urlItems.map(i => i.slug);
    const strategyMapping = {};
    urlItems.forEach(i => {
      if (i.strategyCollection) strategyMapping[i.slug] = { collection: i.strategyCollection, mode: i.syncMode || 'replace' };
    });
    const strategyPayload = urlItems.map(i => {
      const strategy = strategies.find(s => s.slug === i.strategyCollection) || {};
      return {
        slug: i.slug,
        collection: i.strategyCollection || i.slug,
        strategyCollection: i.strategyCollection || '',
        name: strategy.name || i.strategyCollection || i.slug,
        chartinkUrl: i.url,
      };
    });

    try {
      const res = await api.post('/chartink/fetch-all', { strategies: strategyPayload, slugs, strategyMapping });
      const json = res.data;

      if (!json.success) {
        showToast(json.message || 'Fetch failed', 'error');
        setUrlItems(prev => prev.map(i => ({ ...i, status: 'error' })));
        return;
      }

      const newResults = [];
      json.results.forEach((result, idx) => {
        setProgress({ done: idx + 1, total: slugs.length });
        newResults.push(result);
        setUrlItems(prev => prev.map(i => {
          if (i.slug !== result.slug) return i;
          return result.success
            ? { ...i, status: 'success', stocks: result.stocks || [], sync: result.sync || null }
            : { ...i, status: 'error', error: result.error, sync: null };
        }));
      });

      setResults(newResults);
      setLastFetch(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));

      const synced = json.results.filter(r => r.sync && !r.sync.error).length;
      const syncMsg = synced ? ` · ${synced} synced to Strategy DB` : '';
      showToast(`Fetched ${json.fetched}/${json.total} screeners${syncMsg}`, json.failed ? 'info' : 'success');
    } catch (err) {
      showToast('Network error: ' + (err.response?.data?.message || err.message), 'error');
      setUrlItems(prev => prev.map(i => i.status === 'fetching' ? { ...i, status: 'error' } : i));
    } finally {
      setFetching(false);
    }
  };

  /* ── derived stats ── */
  const successItems  = urlItems.filter(i => i.status === 'success');
  const allStocks     = successItems.flatMap(i => i.stocks);
  const gainers       = allStocks.filter(s => (s.percentChange ?? 0) >= 0).length;
  const losers        = allStocks.length - gainers;
  const showStats     = successItems.length > 0;
  const showPlaceholder = results.length === 0;

  const statusLabel = (item) => {
    if (item.status === 'fetching') return <><span className="fsd-spinner" /></>;
    if (item.status === 'success')  return `✓ ${item.stocks.length} stocks`;
    if (item.status === 'error')    return '✗ Failed';
    return '—';
  };

  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  /* ── render ── */
  return (
    <AppLayout pageBadge="📊 Fetch Strategy Data">
      <div className="fsd-root">
        <div className="fsd-main">

          {/* ───── LEFT SIDEBAR ───── */}
          <aside className="fsd-sidebar">

            {/* Add URL */}
            <div className="fsd-card">
              <div className="fsd-card-title">🔗 Add Chartink URL</div>
              <div className="fsd-url-row">
                <input
                  ref={inputRef}
                  className="fsd-url-input"
                  type="text"
                  placeholder="https://chartink.com/screener/…"
                  value={urlInput}
                  onChange={e => setUrlInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddUrl()}
                  autoComplete="off"
                  spellCheck="false"
                />
                <button className="fsd-btn fsd-btn-primary" onClick={handleAddUrl}>Add</button>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                Paste full Chartink URL or just the slug
              </div>
            </div>

            {/* Preset Screeners */}
            <div className="fsd-card">
              <div className="fsd-card-title">⚡ Active Strategies {strategyLoading && <span>Loading...</span>}</div>
              <div className="fsd-preset-grid">
                {strategies.map(strategy => {
                  const slug  = extractSlug(strategy.chartinkUrl);
                  const added = !!urlItems.find(i => i.slug === slug);
                  return (
                    <button
                      key={slug}
                      className={`fsd-preset-btn${added ? ' added' : ''}`}
                      onClick={() => addPreset(strategy)}
                      title={strategy.chartinkUrl}
                    >
                      <span className="fsd-preset-name">{strategy.icon} {strategy.name}</span>
                      {added
                        ? <span className="fsd-preset-tick">✓ Added</span>
                        : <span className="fsd-preset-coll">{strategy.slug}</span>
                      }
                    </button>
                  );
                })}
              </div>
              <button className="fsd-add-all-btn" onClick={addAllPresets}>+ Add All Active Strategies</button>
            </div>

            {/* URL List */}
            <div className="fsd-card" style={{ flex: 1 }}>
              <div className="fsd-card-title">
                <span>📋 Screener List</span>
                <button className="fsd-btn fsd-btn-ghost" style={{ padding: '2px 8px', fontSize: '0.68rem' }} onClick={clearAll}>Clear All</button>
              </div>

              <div className="fsd-url-list">
                {urlItems.length === 0 ? (
                  <div className="fsd-empty-state">No screeners added yet.<br />Paste a Chartink URL above.</div>
                ) : urlItems.map(item => (
                  <div key={item.slug} className={`fsd-url-item ${item.status}`}>
                    <div className="fsd-item-info">
                      <div className="fsd-item-slug">{item.slug}</div>
                      <div className="fsd-item-url">{item.url}</div>
                    </div>
                    <div className={`fsd-item-status ${item.status}`}>{statusLabel(item)}</div>
                    <button className="fsd-btn fsd-btn-danger" onClick={() => removeUrl(item.slug)} title="Remove">✕</button>

                    <div className="fsd-collection-row">
                      <span className="fsd-coll-label">Sync →</span>
                      <select
                        className="fsd-coll-select"
                        value={item.strategyCollection}
                        onChange={e => setCollection(item.slug, e.target.value)}
                      >
                        <option value="">— No Sync —</option>
                        {strategies.map(c => (
                          <option key={c.slug} value={c.slug}>{c.name}</option>
                        ))}
                      </select>

                      {item.strategyCollection && (
                        <div className="fsd-mode-toggle">
                          <button
                            className={`fsd-mode-btn${item.syncMode !== 'replace' ? ' append' : ''}`}
                            onClick={() => setSyncMode(item.slug, 'append')}
                            title="Keep history, refresh today's rows"
                          >Append</button>
                          <button
                            className={`fsd-mode-btn${item.syncMode === 'replace' ? ' replace' : ''}`}
                            onClick={() => setSyncMode(item.slug, 'replace')}
                            title="Wipe and replace collection"
                          >Replace</button>
                        </div>
                      )}

                      {item.sync && (
                        <span className={`fsd-sync-badge${item.sync.error ? ' err' : ''}`}>
                          {item.sync.error ? '✗ sync' : `✓ ${item.sync.synced} saved`}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fetch All */}
            <div className="fsd-card">
              <button
                className="fsd-fetch-all-btn"
                onClick={fetchAll}
                disabled={urlItems.length === 0 || fetching}
              >
                {fetching
                  ? <><span className="fsd-spinner" />Fetching…</>
                  : '⚡ Fetch All Screeners'}
              </button>

              {fetching && (
                <>
                  <div className="fsd-progress-wrap">
                    <div className="fsd-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <div className="fsd-progress-label">{progress.done} / {progress.total} fetched</div>
                </>
              )}

              {lastFetch && !fetching && (
                <div className="fsd-progress-label">Last fetch: {lastFetch}</div>
              )}
            </div>
          </aside>

          {/* ───── RIGHT PANEL ───── */}
          <main className="fsd-results">

            {/* Stats Bar */}
            {showStats && (
              <div className="fsd-stats-bar">
                <div className="fsd-stat-card">
                  <div className="fsd-stat-value">{successItems.length}</div>
                  <div className="fsd-stat-label">Screeners</div>
                </div>
                <div className="fsd-stat-card">
                  <div className="fsd-stat-value green">{allStocks.length}</div>
                  <div className="fsd-stat-label">Total Stocks</div>
                </div>
                <div className="fsd-stat-card">
                  <div className="fsd-stat-value green">{gainers}</div>
                  <div className="fsd-stat-label">Gainers</div>
                </div>
                <div className="fsd-stat-card">
                  <div className="fsd-stat-value red">{losers}</div>
                  <div className="fsd-stat-label">Losers</div>
                </div>
              </div>
            )}

            {/* Placeholder */}
            {showPlaceholder && (
              <div className="fsd-placeholder">
                <div className="fsd-placeholder-icon">📈</div>
                <strong>No results yet</strong>
                <span>Add Chartink screener URLs on the left, then click<br /><strong>⚡ Fetch All Screeners</strong></span>
              </div>
            )}

            {/* Screener result blocks */}
            {results.map(result => (
              <ScreenerBlock key={result.slug} result={result} />
            ))}
          </main>
        </div>

        {/* Toasts */}
        <div className="fsd-toast-wrap">
          {toasts.map(t => (
            <div key={t.id} className={`fsd-toast ${t.type}`}>{t.msg}</div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
